const DEFAULT_OPTIONS = {
  onlyMobile: true,
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.82,
  minBytes: 700 * 1024
};

const isMobileLikeDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const byTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const byUA = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
  return Boolean(byTouch || byUA);
};

const changeExtension = (name, ext) => {
  const safe = String(name || 'upload');
  const i = safe.lastIndexOf('.');
  const base = i > 0 ? safe.slice(0, i) : safe;
  return `${base}.${ext}`;
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

export const maybeResizeImageFile = async (file, options = {}) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (opts.onlyMobile && !isMobileLikeDevice()) return file;

  let img;
  try {
    img = await loadImage(file);
  } catch (e) {
    return file;
  }

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) return file;

  const ratio = Math.min(1, opts.maxWidth / srcW, opts.maxHeight / srcH);
  const shouldScale = ratio < 1;
  const shouldCompress = file.size >= opts.minBytes;
  if (!shouldScale && !shouldCompress) return file;

  const targetW = Math.max(1, Math.round(srcW * ratio));
  const targetH = Math.max(1, Math.round(srcH * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await toBlob(canvas, outputType, opts.quality);
  if (!blob || blob.size <= 0) return file;
  if (blob.size >= file.size && !shouldScale) return file;

  const ext = outputType === 'image/png' ? 'png' : 'jpg';
  return new File([blob], changeExtension(file.name, ext), {
    type: outputType,
    lastModified: Date.now()
  });
};

export const prepareImagesForUpload = async (files, options = {}) => {
  const list = Array.from(files || []);
  const out = [];
  for (const file of list) {
    // Sequential processing to avoid memory spikes on weak mobile devices.
    // eslint-disable-next-line no-await-in-loop
    const processed = await maybeResizeImageFile(file, options);
    out.push(processed);
  }
  return out;
};
